import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Users, ChevronRight, Loader2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Short, calm "Next — {title} · {date}" formatter. No relative time.
function formatNextActivity(next) {
  if (!next) return '';
  const title = (next.title || '').trim();
  let when = '';
  try {
    const d = new Date(next.start_time);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    when = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: sameYear ? undefined : 'numeric',
    });
  } catch { /* ignore */ }
  return title && when ? `${title} · ${when}` : (title || when);
}

/**
 * SupportSponsor — a single sponsor page.
 *
 * Neutral, factual. Per directive:
 * - name, categories, groups currently supported (linked), optional short note
 * - NO analytics, performance, promotional copy, "why choose us"
 *
 * When a group_id is present in the URL, shows the request action:
 * "Ask to support this group" → inline single-line note + "Send request".
 */
export default function SupportSponsor() {
  const navigate = useNavigate();
  const { sponsorId } = useParams();
  const [params] = useSearchParams();
  const groupId = params.get('group_id');
  const [sponsor, setSponsor] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [showRequest, setShowRequest] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/support/sponsors/${sponsorId}`, { withCredentials: true })
      .then(r => setSponsor(r.data))
      .catch(() => setSponsor(null));
  }, [sponsorId]);

  useEffect(() => {
    if (!groupId) return;
    axios.get(`${API}/api/groups/${groupId}/profile`, { withCredentials: true })
      .then(r => {
        setGroupName(r.data?.name || '');
        setGroupDesc(r.data?.description || '');
      })
      .catch(() => { /* silent */ });
  }, [groupId]);

  const send = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/support/requests`,
        { group_id: groupId, sponsor_id: sponsorId, note: note || null },
        { withCredentials: true },
      );
      setSent(true);
      toast.success('Ask sent.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not send ask.';
      toast.error(typeof msg === 'string' ? msg : 'Could not send ask.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!sponsor) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--fy-text-secondary)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="support-sponsor-page">
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back" data-testid="support-sponsor-back">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
          <h1 className="text-base font-semibold truncate flex-1" style={{ color: 'var(--fy-text-primary)' }}>
            {sponsor.name}
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Identity */}
        <section className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            {sponsor.logo_url ? (
              <img src={sponsor.logo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <Users className="w-6 h-6" style={{ color: 'var(--fy-text-secondary)' }} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-extrabold" style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit, system-ui' }}>
              {sponsor.name}
            </h2>
            {sponsor.categories?.length > 0 && (
              <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                {sponsor.categories.join(' · ')}
              </p>
            )}
          </div>
        </section>

        {/* Optional note (1–2 lines max, no promotional language) */}
        {sponsor.note && (
          <p className="text-sm leading-relaxed" style={{ color: 'var(--fy-text-primary)' }} data-testid="support-sponsor-note">
            {sponsor.note}
          </p>
        )}

        {/* Groups supported */}
        {sponsor.groups_supported?.length > 0 && (
          <section aria-label="Groups currently supported" data-testid="support-sponsor-groups">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 px-1" style={{ color: 'var(--fy-text-secondary)' }}>
              Supporting {sponsor.groups_supported.length} {sponsor.groups_supported.length === 1 ? 'group' : 'groups'}
            </h3>
            <ul className="space-y-2">
              {sponsor.groups_supported.map(g => (
                <li key={g.group_id}>
                  <Link
                    to={`/group/${g.group_id}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                    data-testid={`support-sponsor-group-${g.group_id}`}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                      {g.cover_image ? (
                        <img src={g.cover_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>
                        {g.name}
                      </p>
                      {g.next_activity && (
                        <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--fy-text-secondary)' }}>
                          Next — {formatNextActivity(g.next_activity)}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Ask action — only when we know which group is asking */}
        {groupId && !sent && !showRequest && (
          <button
            type="button"
            onClick={() => setShowRequest(true)}
            className="w-full px-4 py-3 rounded-full text-sm font-bold transition-transform active:scale-[0.99]"
            style={{ backgroundColor: '#8B6A4F', color: '#FFFFFF' }}
            data-testid="support-ask-button"
          >
            Ask to support this group
          </button>
        )}

        {groupId && showRequest && !sent && (
          <div
            className="rounded-2xl p-4 space-y-3"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="support-request-panel"
          >
            <div>
              <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--fy-text-disabled)' }}>
                Asking on behalf of
              </p>
              <p className="text-base font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                {groupName || 'Your group'}
              </p>
              {groupDesc && (
                <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--fy-text-secondary)' }}>
                  {groupDesc}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold" style={{ color: 'var(--fy-text-secondary)' }} htmlFor="support-note">
                Add a short note (optional)
              </label>
              <input
                id="support-note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 200))}
                placeholder=""
                maxLength={200}
                className="mt-1 w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                data-testid="support-note-input"
              />
            </div>
            <button
              type="button"
              onClick={send}
              disabled={submitting}
              className="w-full px-4 py-3 rounded-full text-sm font-bold transition-transform active:scale-[0.99] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: '#8B6A4F', color: '#FFFFFF' }}
              data-testid="support-send-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Send request
            </button>
          </div>
        )}

        {sent && (
          <div
            className="rounded-2xl p-4 flex items-start gap-3"
            style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-sage)' }}
            data-testid="support-request-sent"
          >
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                Ask sent
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                {sponsor.name} will get back to you with Yes or No.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
